import { Controller } from "@hotwired/stimulus"

// The assistant rail's composer (TASK-268.1, decision-78). One textarea, a
// paperclip that opens the file picker with the chosen files listed as chips,
// and a send button. Enter sends (Shift+Enter is a new line); the box grows
// with its text up to a few lines.
//
// The assistant renders twice (the desktop rail and the phone sheet), so
// everything here is found through targets, never ids — each copy has its own
// controller.
//
// On a PUBLISHED version the panel carries data-assistant-version, and every
// message says which version it is about (message[version]), so a form with an
// open draft still plans against the published version on show (TASK-268.4).
export default class extends Controller {
  static targets = ["form", "text", "files", "chips"]
  static values = { locked: Boolean }

  connect() {
    this.addVersion = (event) => {
      const version = this.element.closest("[data-assistant-version]")?.dataset.assistantVersion
      if (version) event.formData.set("message[version]", version)
    }
    this.versionForm = this.hasFormTarget ? this.formTarget : null
    this.versionForm?.addEventListener("formdata", this.addVersion)
    this.grow()
    this.syncChips()
    if (!this.lockedValue && this.hasTextTarget && this.textTarget.value.length > 0) this.textTarget.focus()
  }

  disconnect() {
    this.versionForm?.removeEventListener("formdata", this.addVersion)
  }

  pick(event) {
    event.preventDefault()
    if (this.hasFilesTarget) this.filesTarget.click()
  }

  filesChanged() {
    this.renderChosenFiles()
  }

  keydown(event) {
    if (event.key !== "Enter" || event.shiftKey || event.isComposing) return
    if (this.lockedValue || this.textTarget.value.trim().length === 0) return

    event.preventDefault()
    this.formTarget.requestSubmit()
  }

  grow() {
    if (!this.hasTextTarget) return
    const box = this.textTarget
    box.style.height = "auto"
    box.style.height = `${Math.min(box.scrollHeight + 2, 168)}px`
  }

  removeFile(event) {
    event.preventDefault()
    const index = Number(event.currentTarget.dataset.index)
    const kept = new DataTransfer()
    Array.from(this.filesTarget.files).forEach((file, i) => { if (i !== index) kept.items.add(file) })
    this.filesTarget.files = kept.files
    this.renderChosenFiles()
  }

  // The chips for files chosen in this copy of the composer. Server-rendered
  // chips (files carried over from a failed attempt) have no data-chosen
  // mark and are left alone.
  renderChosenFiles() {
    this.chipsTarget.querySelectorAll("[data-chosen]").forEach((chip) => chip.remove())

    Array.from(this.filesTarget.files).forEach((file, index) => {
      const chip = document.createElement("span")
      chip.dataset.chosen = "true"
      chip.className = "inline-flex max-w-full items-center gap-1 rounded border border-gray-300 bg-white px-1.5 py-0.5 text-[11px] font-semibold text-gray-700"

      const name = document.createElement("span")
      name.className = "truncate"
      name.textContent = file.name
      chip.appendChild(name)

      const remove = document.createElement("button")
      remove.type = "button"
      remove.className = "shrink-0 text-gray-400 hover:text-gray-700"
      remove.setAttribute("aria-label", `Remove ${file.name}`)
      remove.dataset.index = String(index)
      remove.dataset.action = "click->assistant-composer#removeFile"
      remove.textContent = "×"
      chip.appendChild(remove)

      this.chipsTarget.appendChild(chip)
    })
    this.syncChips()
  }

  syncChips() {
    if (this.hasChipsTarget) this.chipsTarget.hidden = this.chipsTarget.children.length === 0
  }
}
